import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
  Column,
  Heading,
  Icon,
  InputTyping,
  Modal,
  Row,
} from '@drivesally/sally-ui';

import {grey} from '@drivesally/sally-ui/lib/libs/colors';

const DEFAULT_TAG_COLOR = '#6B7280';

const Loader = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  padding: 32px;
`;

const NewTagRow = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
`;

const TagRow = styled.div`
  align-items: center;
  border-bottom: 1px solid ${grey};
  display: flex;
  gap: 12px;
  padding: 12px 0;
`;

const TagNameInput = styled.input`
  border: 1px solid ${grey};
  border-radius: 4px;
  flex: 1;
  font-size: 14px;
  padding: 8px 10px;

  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
  }

  &:focus {
    outline: none;
  }
`;

const ColorInput = styled.input.attrs({type: 'color'})`
  border: 1px solid ${grey};
  border-radius: 4px;
  cursor: pointer;
  flex-shrink: 0;
  height: 32px;
  padding: 2px;
  width: 32px;

  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }

  &::-webkit-color-swatch {
    border: none;
    border-radius: 2px;
  }
`;

const DeleteButton = styled.button`
  align-items: center;
  background: none;
  border: none;
  color: #888;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  padding: 4px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:hover:not(:disabled) {
    color: #e74c3c;
  }
`;

const EmptyState = styled.div`
  color: #888;
  font-size: 14px;
  padding: 16px 0;
`;

const CrmTagsModal = ({
  creating,
  deletingId,
  loading,
  newTag,
  newTagColor,
  onClose,
  onCreate,
  onDelete,
  onNewTagChange,
  onNewTagColorChange,
  onTagColorChange,
  onTagNameBlur,
  onTagNameChange,
  tagEdits,
  tags,
  updatingId,
  visible,
}) => (
  <Modal
    title="CRM Tags"
    icon="tag"
    visible={visible}
    onClose={onClose}
    buttonsRight={[
      {
        label: 'Add Tag',
        loading: creating,
        onClick: onCreate,
      },
    ]}
  >
    {(Content) => (
      <Content>
        <Row margin>
          <Column>
            <NewTagRow>
              <ColorInput
                value={newTagColor || DEFAULT_TAG_COLOR}
                onChange={onNewTagColorChange}
                disabled={creating || loading}
                title="Tag color"
              />
              <InputTyping
                value={newTag}
                onChange={onNewTagChange}
                placeholder="New tag label"
                disabled={creating || loading}
              />
            </NewTagRow>
          </Column>
        </Row>
        <Row margin>
          <Column>
            <Heading size="4">Existing Tags</Heading>
          </Column>
        </Row>
        <Row>
          <Column>
            {loading ? (
              <Loader>
                <Icon icon="loading2" spin />
              </Loader>
            ) : !tags.length ? (
              <EmptyState>No tags yet.</EmptyState>
            ) : (
              tags.map((tag) => (
                <TagRow key={tag.id}>
                  <ColorInput
                    value={tag.color || DEFAULT_TAG_COLOR}
                    onChange={onTagColorChange(tag)}
                    disabled={
                      !!deletingId || creating || updatingId === tag.id
                    }
                    title="Tag color"
                  />
                  <TagNameInput
                    value={
                      tagEdits[tag.id] !== undefined ? tagEdits[tag.id] : tag.tag
                    }
                    onChange={(event) => onTagNameChange(tag)(event.target.value)}
                    onBlur={onTagNameBlur(tag)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') event.target.blur();
                    }}
                    disabled={
                      !!deletingId || creating || updatingId === tag.id
                    }
                  />
                  <DeleteButton
                    type="button"
                    disabled={!!deletingId || creating || updatingId === tag.id}
                    onClick={onDelete(tag)}
                  >
                    <Icon
                      icon={deletingId === tag.id ? 'loading2' : 'delete'}
                      spin={deletingId === tag.id}
                    />
                  </DeleteButton>
                </TagRow>
              ))
            )}
          </Column>
        </Row>
      </Content>
    )}
  </Modal>
);

CrmTagsModal.propTypes = {
  creating: PropTypes.bool,
  deletingId: PropTypes.number,
  loading: PropTypes.bool,
  newTag: PropTypes.string,
  newTagColor: PropTypes.string,
  onClose: PropTypes.func,
  onCreate: PropTypes.func,
  onDelete: PropTypes.func,
  onNewTagChange: PropTypes.func,
  onNewTagColorChange: PropTypes.func,
  onTagColorChange: PropTypes.func,
  onTagNameBlur: PropTypes.func,
  onTagNameChange: PropTypes.func,
  tagEdits: PropTypes.object,
  tags: PropTypes.array,
  updatingId: PropTypes.number,
  visible: PropTypes.bool,
};

export default CrmTagsModal;
